/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2021 ICEMEDX, Inc.
***********************************************/
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import {
  IcecontrolSettings,
  iceColumnDefinition,
  IcemattableSettings,
  IcepanelgroupSettings,
  IcepanelsectionSettings,
  IceexpansionpanelSettings,
  IcepanelcontainerRowSettings,
  IcepanelcontainerSettings,
  IcecontrolPermission,
  SECTION_CONTROL_ALIGNMENT,
  IcepanelSettings
} from 'projects/icemedx-lib/src/components/models/icesettings';

import { IceEntity } from 'projects/icemedx-lib/src/models/core/IceEntity';
import { vendor } from 'projects/icemedx-lib/src/models/domain/vendor';
import { entity } from 'projects/icemedx-lib/src/models/common/entity';
import { contact } from 'projects/icemedx-lib/src/models/common/contact';
import { address } from '../../../../icemedx-lib/src/models/common/address';

import { AppService } from '../app.service';
import { IceNavService } from '../../../../icemedx-lib/src/components/ice-nav/ice-nav.service';
import { LoginService } from '../../../../icemedx-lib/src/login/login.service';
import { DomainService } from 'projects/icemedx-lib/src/services/domain/domain.service';
import { VendorService } from '../../../../icemedx-lib/src/services/vendor/vendor.service';
import { ProviderService } from '../../../../icemedx-lib/src/services/provider/provider.service';
import { PharmacyService } from '../../../../icemedx-lib/src/services/pharmacy/pharmacy.service';
import { PayerService } from '../../../../icemedx-lib/src/services/payer/payer.service';
import { IceTypes } from 'projects/icemedx-lib/src/models/icetypes';
import { userTypes } from 'projects/icemedx-lib/src/login/login.config';

import { DomainProfile } from '../domainprofile';
import { MedicalcodesService } from '../../../../icemedx-lib/src/services/medicalcodes/medicalcodes.service';
import { basecode } from '../../../../icemedx-lib/src/models/ehr/subs/basecode';
import { MessageService } from '../../../../icemedx-lib/src/services/message/message.service';

@Component({
  selector: 'app-vendor',
  templateUrl: './vendor.component.html',
  styleUrls: ['./vendor.component.scss'],
  providers: [AppService, IceNavService]
})
export class VendorComponent extends DomainProfile implements OnInit {
  // Data Members
  vendorObj: vendor = null;

  profileViewSettings: IcepanelcontainerSettings = null;
  btnSaveProfile: IcecontrolSettings = new IcecontrolSettings("SaveButton", { text: "Save Profile", class: [], glyphClass: "fa-plus-circle" }, null, null);

  constructor(appSvc: AppService,
    domainSvc: DomainService,
    vendorSvc: VendorService,
    proSvc: ProviderService,
    pharmSvc: PharmacyService,
    payerSvc: PayerService, 
    medCodeSvc: MedicalcodesService,
    messageSvc: MessageService,
    loginSvc: LoginService,
    navSvc: IceNavService,
    router: Router) {
    super(appSvc, domainSvc, vendorSvc, proSvc, pharmSvc, payerSvc, medCodeSvc, messageSvc, loginSvc, navSvc, router);

    appSvc.defaultMenus();
    appSvc.addBreadcrumb("Vendor Profile", {}, true, "");

    domainSvc.getVendor(appSvc.getCurrentAccount()).subscribe(
      pro => {
        this.profileViewSettings = this.viewSetup(pro);
      },
      error => {
      });
  }

  ngOnInit() {
    this.getAppService().refreshMenus();
  }

  viewSetup(dat: IceEntity): IcepanelcontainerSettings {
    let setup: IcepanelcontainerSettings = null;
    try {
      let feaAccess = this.getAppService().getPermission(userTypes.USER_FEATURES.ufSetup).default_access;
      this.btnSaveProfile.permissions = new IcecontrolPermission(userTypes.USER_FEATURES.ufSetup, feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone ? userTypes.USER_FEATURE_ACCESS.ufaReadOnly : feaAccess);

      if (feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone) {
        this.getDomainService().logMessage(IceTypes.LOGTYPE.ltError, "You do not have access to view this data.");
        return null;
      }
      if (dat == null || dat["length"] == 0) {
        return null;
      }

      this.vendorObj = <vendor>dat[0];

      //////////////////////
      // Name
      if (this.vendorObj.COUNTRIES == null || this.vendorObj.COUNTRIES.length == 0) {
        this.vendorObj.COUNTRIES = [new basecode("COUNTRY")];
      }
      if (this.vendorObj.CURRENCIES == null || this.vendorObj.CURRENCIES.length == 0) {
        this.vendorObj.CURRENCIES = [new basecode("CURRENCY")];
      }

      let cntryCode: basecode = this.vendorObj.COUNTRIES[0];
      let curcyCode: basecode = this.vendorObj.CURRENCIES[0];
      let nameControls = super.viewName(this.vendorObj.trans_account, this.vendorObj.ENTS, this.vendorObj.entity_key, false, "", cntryCode, curcyCode);
      let nameSections: IcepanelsectionSettings[] = [
        new IcepanelsectionSettings("NameViewSection", { class: ["yalla"], styles: { width: "100%" } }, null, nameControls, null)
      ];

      //////////////////////
      // Contact Info
      let contactTables = super.viewContact(this.vendorObj.CONS);
      let contactSections = this.viewContactSection(contactTables, true);

      //////////////////////
      // Address Info
      let addressTables = this.viewAddress(this.vendorObj.ADS);
      let addressSections = this.viewAddressSection(addressTables, true);

      //////////////////////
      // Identification Info
      let idCodeList = ['46', 'FI'];
      let idTables = super.viewIdentification(this.vendorObj.IDS, idCodeList);
      let idSections = this.viewIdentificationSection(idTables, idCodeList, true);

      /////////////////////////
      // Network Connections
      let networkConnSections = this.viewNewOutboundNetworkConnections();
      let outboundNetworkConnectionTables = this.viewOutboundNetworkConnections(this.vendorObj.CONNPARAMS, this.vendorObj.TYPEREFS);
      let outboundGroups: IcepanelgroupSettings[] = [
        new IcepanelgroupSettings(null, outboundNetworkConnectionTables)
      ];

      networkConnSections.push(new IcepanelsectionSettings("OutboundNetworkConnectionViewSection", { class: ["yalla"], styles: { width: "100%" } }, outboundGroups, [], null));
      networkConnSections = networkConnSections.concat(this.viewEditOutboundNetworkConnections());

      let inboundNetworkConnectionTables = this.viewInboundNetworkConnections(this.vendorObj.INCONNPARAMS, this.vendorObj.TYPES);
      let inboundGroups: IcepanelgroupSettings[] = [
        new IcepanelgroupSettings(null, inboundNetworkConnectionTables)
      ];
      networkConnSections.push(new IcepanelsectionSettings("InboundNetworkConnectionViewSection", { class: ["yalla"], styles: { width: "100%" } }, inboundGroups, [], null));
      networkConnSections = networkConnSections.concat(this.viewEditInboundNetworkConnections());

      let namefrm: IcepanelSettings = new IceexpansionpanelSettings("NameView", "", "Name", "", nameSections, null, null, null)
        .enableForm();
      let nameFrmGrp = namefrm.getForm();
      let panels: IcepanelSettings[] = [
        namefrm,
        new IceexpansionpanelSettings("ContactView", "", "Contact Information", "", contactSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("AddressView", "", "Address Information", "", addressSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("IdentificationView", "", "Identification Information", "", idSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("NetworkConnectionView", "", "Network Connections", "", networkConnSections, null, null, null)
          .enableForm(nameFrmGrp),
      ];

      let panelrows: IcepanelcontainerRowSettings[] = [new IcepanelcontainerRowSettings(panels)];
      setup = new IcepanelcontainerSettings("ContactViewContainer", panelrows);
    }
    catch (e) {
      console.log(e);
    }
    return setup;
  }

  // Events
  onControlChange(event) {
    super.onControlChange(event);
  }

  onButtonClick(event) {
    this.onDefaultButtonClick(event, this.vendorObj.CONNPARAMS, this.vendorObj.TYPEREFS, this.editInboundRow);
  }

  public onRowClick(event) {
    super.onRowClick(event);
  }

  public onAddRowButtonClick(event) {
    this.onDefaultAddRowButtonClick(event,
      this.vendorObj.CONS,
      this.vendorObj.ADS,
      this.vendorObj.IDS,
      this.vendorObj.CONNPARAMS,
      this.vendorObj.TYPEREFS,
      this.vendorObj.INCONNPARAMS,
      this.vendorObj.TYPES);
  }

  onSaveProfile(event) {
    if (false == this.updateName(this.vendorObj.COUNTRIES, this.vendorObj.CURRENCIES)) {
      // To do error log
      return;
    }

    this.updateContact(this.vendorObj.CONS);
    this.updateAddress(this.vendorObj.ADS);
    this.updateIdentification(this.vendorObj.IDS);
    this.updateOutboundDomainConnection(this.vendorObj.CONNPARAMS, this.vendorObj.TYPEREFS);
    this.updateInboundDomainConnection(this.vendorObj.INCONNPARAMS, this.vendorObj.TYPES);
    this.getVendorService().updateVendor(this.vendorObj).subscribe(
      data => {
        this.getRouter().navigate([this.getNavService().buildRoute("dashboard", false)]);
      },
      error => {
        console.log(error);
      });
  }
}
